import React, { useState } from 'react';

interface ValidatedFieldProps {
  id: string;
  name: string;
  type: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  autoComplete?: string;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
  showValidation?: boolean;
  serverErrors?: string[];
  hint?: string;
}

const ValidatedField: React.FC<ValidatedFieldProps> = ({
  id,
  name,
  type,
  label,
  placeholder,
  value,
  onChange,
  required = false,
  autoComplete,
  disabled = false,
  className = '',
  autoFocus = false,
  showValidation = true,
  serverErrors = [],
  hint,
}) => {
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [hasServerErrors, setHasServerErrors] = useState(false);

  // Track when server errors are received
  React.useEffect(() => {
    if (serverErrors && serverErrors.length > 0) {
      setHasServerErrors(true);
    }
  }, [serverErrors]);

  const isEmpty = value.length === 0;

  // Red border only when server errors exist and user hasn't changed field
  const hasServerValidationErrors =
    hasServerErrors && serverErrors && serverErrors.length > 0;

  // Show hint when field is focused, invalid, or has server errors
  const shouldShowHint = hasBeenFocused || hasServerValidationErrors;

  let hintMessage = '';
  let isHintRed = false;

  if (shouldShowHint) {
    // Prioritize server errors if they exist
    if (hasServerValidationErrors) {
      hintMessage = serverErrors[0];
      isHintRed = true;
    } else if (hasBeenFocused && required && isEmpty) {
      hintMessage = hint || `${label} is required.`;
      isHintRed = false;
    } else if (hasBeenFocused && hint) {
      hintMessage = hint;
      isHintRed = false;
    }
  }

  // Handle input change - clear server error state when user types
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasServerErrors(false);
    onChange(e);
  };

  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div>
        <input
          id={id}
          name={name}
          type={type}
          autoComplete={autoComplete}
          required={required}
          value={value}
          onChange={handleChange}
          onFocus={() => setHasBeenFocused(true)}
          className={`appearance-none block w-full px-3 py-2 border rounded-md placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
            hasServerValidationErrors
              ? 'border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-indigo-500 focus:border-indigo-500'
          }`}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
        />
      </div>
      {showValidation && shouldShowHint && hintMessage && (
        <p
          className={`mt-1 text-xs ${
            isHintRed
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          {hintMessage}
        </p>
      )}
    </div>
  );
};

export default ValidatedField;
